import { z } from 'zod';

export const CurrencyDenominationSchema = z.union([
  z.literal(200),
  z.literal(500),
  z.literal(1000),
  z.literal(2000),
  z.literal(5000),
  z.literal(10000),
  z.literal(20000),
  z.literal(50000),
]);

export type CurrencyDenomination = z.infer<typeof CurrencyDenominationSchema>;

export const TipoEquipamentoCodigoSchema = z
  .string()
  .regex(/^[A-Z0-9_]{2,32}$/);

export const TipoEquipamentoSchema = z.object({
  tipoId: z.string().uuid(),
  codigo: TipoEquipamentoCodigoSchema,
  nome: z.string().min(1),
  descricao: z.string().optional(),
  temCofre: z.boolean(),
  temValidador: z.boolean(),
  temDispensador: z.boolean(),
  temImpressora: z.boolean(),
  temTela: z.boolean(),
  capacidadeOperacionalDefault: z.number().int().min(0),
  capacidadeCedulasDefault: z.number().int().nonnegative().optional(),
  valorSeguroDefault: z.number().int().nonnegative().optional(),
  qtdMaxCedulasPorDeposito: z.number().int().min(1),
  moedasAceitasDefault: z.array(CurrencyDenominationSchema),
  tiposPagamentoAceitos: z.array(z.string().min(1)),
  layoutId: z.string().uuid().optional(),
  layoutSvgUrl: z.string().url().optional(),
  anyDeskSupported: z.boolean(),
  ativo: z.boolean().default(true),
  systemLocked: z.boolean().default(false),
  createdAt: z.string().datetime(),
  createdBy: z.string().min(1),
  updatedAt: z.string().datetime().optional(),
  updatedBy: z.string().min(1).optional(),
});

export type TipoEquipamento = z.infer<typeof TipoEquipamentoSchema>;

export const TipoEquipamentoCreateSchema = z
  .object({
    codigo: TipoEquipamentoCodigoSchema,
    nome: z.string().min(1),
    descricao: z.string().optional(),
    temCofre: z.boolean(),
    temValidador: z.boolean(),
    temDispensador: z.boolean(),
    temImpressora: z.boolean(),
    temTela: z.boolean(),
    capacidadeOperacionalDefault: z.number().int().min(0),
    capacidadeCedulasDefault: z.number().int().nonnegative().optional(),
    valorSeguroDefault: z.number().int().nonnegative().optional(),
    qtdMaxCedulasPorDeposito: z.number().int().min(1),
    moedasAceitasDefault: z.array(CurrencyDenominationSchema),
    tiposPagamentoAceitos: z.array(z.string().min(1)),
    layoutId: z.string().uuid().optional(),
    layoutSvgUrl: z.string().url().optional(),
    anyDeskSupported: z.boolean(),
  })
  .refine(
    (data) =>
      data.temCofre
      || data.temValidador
      || data.temDispensador
      || data.temImpressora
      || data.temTela
      || data.codigo === 'CUSTOM',
    { message: 'At least one peripheral must be enabled, unless codigo is CUSTOM' },
  );

export type TipoEquipamentoCreateInput = z.infer<typeof TipoEquipamentoCreateSchema>;

export const TipoEquipamentoPatchSchema = z
  .object({
    nome: z.string().min(1).optional(),
    descricao: z.string().optional(),
    temCofre: z.boolean().optional(),
    temValidador: z.boolean().optional(),
    temDispensador: z.boolean().optional(),
    temImpressora: z.boolean().optional(),
    temTela: z.boolean().optional(),
    capacidadeOperacionalDefault: z.number().int().min(0).optional(),
    capacidadeCedulasDefault: z.number().int().nonnegative().max(1_000_000).optional(),
    valorSeguroDefault: z.number().int().nonnegative().max(99_999_999_999).optional(),
    qtdMaxCedulasPorDeposito: z.number().int().min(1).optional(),
    moedasAceitasDefault: z.array(CurrencyDenominationSchema).optional(),
    tiposPagamentoAceitos: z.array(z.string().min(1)).optional(),
    layoutId: z.string().uuid().optional(),
    layoutSvgUrl: z.string().url().optional(),
    anyDeskSupported: z.boolean().optional(),
    ativo: z.boolean().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'At least one field required' });

export type TipoEquipamentoPatchInput = z.infer<typeof TipoEquipamentoPatchSchema>;

export const EquipamentoRegistrationSchema = z.object({
  deviceId: z.string().min(1),
  tenantId: z.string().min(1),
  tipoEquipamentoId: z.string().uuid(),
  nome: z.string().min(1),
  hardwareVersion: z.string().min(1),
  serialFabricante: z.string().min(1),
  anyDeskId: z.string().optional(),
  capacidadeOperacional: z.number().int().min(0),
  limiteSeguro: z.number().int().min(0),
  alertaCapacidadePercentual: z.number().int().min(0).max(100),
  qtdMaxCedulasPorDeposito: z.number().int().min(1),
  moedasAceitas: z.array(CurrencyDenominationSchema),
  tiposPagamentoAceitos: z.array(z.string().min(1)),
  impressora: z
    .object({
      protocolo: z.enum(['ESC_POS', 'STAR']),
      viasRecibo: z.union([z.literal(1), z.literal(2)]),
      larguraMm: z.union([z.literal(58), z.literal(80)]),
    })
    .optional(),
  designadorId: z.string().optional(),
  empresaId: z.string().optional(),
  localId: z.string().optional(),
  priority: z.boolean(),
  isDZero: z.boolean(),
  firmwareVersion: z.string().optional(),
  installedAt: z.string().datetime().optional(),
  registeredBy: z.string().min(1),
});

export type EquipamentoRegistration = z.infer<typeof EquipamentoRegistrationSchema>;

/** @deprecated Use EquipamentoRegistration */
export type SafeRegistration = EquipamentoRegistration;

// --- Cadastro de Equipamentos (novo fluxo) ---

/**
 * @deprecated Use VarianteHardwareSchema do catálogo de plataforma (platform-registry).
 * Mantido para compatibilidade com packages/infra/lambdas/shared/types.ts (fleet provisioning).
 */
export const HardwareVariantSchema = z.enum(['MINI_PC', 'MINI_PC_X86', 'TORADEX_ARM']);
/**
 * @deprecated Use VarianteHardware do catálogo de plataforma.
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export type HardwareVariant = z.infer<typeof HardwareVariantSchema>;

export const PriorityCadastroSchema = z.enum(['BAIXA', 'MEDIA', 'ALTA']);
export type PriorityCadastro = z.infer<typeof PriorityCadastroSchema>;

export const RegistrarDeviceInputSchema = z.object({
  tipoEquipamentoId: z.string().uuid(),
  serialFabricante: z.string().min(1),
  hardwareVersion: z.string().min(1),
  firmwareVersion: z.string().optional(),
  anyDeskId: z.string().optional(),
  hardwareVariant: z.string().regex(/^[A-Z0-9_]{2,32}$/),
  priorityCadastro: PriorityCadastroSchema.default('MEDIA'),
  designadorId: z.string().min(1),
  empresaId: z.string().min(1),
  filialId: z.string().min(1),
  localId: z.string().uuid().optional(),
  /** Ambiente da filial onde o cofre fica (spec filial-multiplos-ambientes). `null`/`''` = sem ambiente. */
  ambienteId: z.string().max(64).nullable().optional(),
  capacidadeCedulas: z.number().int().nonnegative().optional(),
  valorSeguro: z.number().int().nonnegative().optional(),
  /** Fonte do CNPJ exibido nos comprovantes do equipamento. */
  cnpjSource: z.enum(['empresa', 'filial']).default('empresa').optional(),
  /** Logo exibida no Kiosk. `null` = sem logo configurada. */
  logoSource: z.enum(['designador', 'empresa', 'filial']).nullable().optional(),
});

export type RegistrarDeviceInput = z.infer<typeof RegistrarDeviceInputSchema>;
